// Detect a straightforward two/multi-number operation in a question, and
// build a step-by-step worked solution (column method) to show the child
// how to get the answer when they are wrong.

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fmt = (n) => Number(n).toLocaleString("en-US");

export function parseArithmetic(text) {
  if (!text) return null;
  const clean = String(text).replace(/,/g, "");
  const sym = clean.match(/(\d+)\s*([+\-−×x*✕⋅·÷/])\s*(\d+)/i);
  if (sym) {
    const map = { "+": "add", "-": "sub", "−": "sub", "×": "mul", x: "mul", "*": "mul", "✕": "mul", "⋅": "mul", "·": "mul", "÷": "div", "/": "div" };
    const op = map[sym[2].toLowerCase()];
    if (op === "add") {
      const chain = clean.match(/\d+(?:\s*\+\s*\d+)+/);
      const addends = (chain ? chain[0] : `${sym[1]}+${sym[3]}`).split("+").map((s) => s.trim());
      return { op: "add", addends };
    }
    if (op) return { op, a: sym[1], b: sym[3] };
  }
  const low = clean.toLowerCase();
  let w;
  if ((w = low.match(/subtract\s+(\d+)\s+from\s+(\d+)/))) return { op: "sub", a: w[2], b: w[1] };
  if ((w = low.match(/(?:add|sum of)\s+(\d+)\s+(?:and|to|\+)\s+(\d+)/))) return { op: "add", addends: [w[1], w[2]] };
  if ((w = low.match(/(\d+)\s+plus\s+(\d+)/))) return { op: "add", addends: [w[1], w[2]] };
  if ((w = low.match(/(\d+)\s+minus\s+(\d+)/))) return { op: "sub", a: w[1], b: w[2] };
  if ((w = low.match(/(?:multiply)\s+(\d+)\s+(?:by|and)\s+(\d+)/))) return { op: "mul", a: w[1], b: w[2] };
  if ((w = low.match(/(\d+)\s+times\s+(\d+)/))) return { op: "mul", a: w[1], b: w[2] };
  if ((w = low.match(/divide\s+(\d+)\s+by\s+(\d+)/))) return { op: "div", a: w[1], b: w[2] };
  return null;
}

const PLACES = {
  en: ["units", "tens", "hundreds", "thousands", "ten-thousands", "hundred-thousands", "millions"],
  fr: ["unités", "dizaines", "centaines", "milliers", "dizaines de mille", "centaines de mille", "millions"],
};

function addSteps(nums, fr) {
  const places = PLACES[fr ? "fr" : "en"];
  const maxLen = Math.max(...nums.map((n) => String(n).length));
  const digits = nums.map((n) => String(n).padStart(maxLen, "0").split("").map(Number));
  const steps = [];
  let carry = 0;
  for (let col = maxLen - 1; col >= 0; col--) {
    const colDigits = digits.map((d) => d[col]);
    const sum = colDigits.reduce((a, b) => a + b, 0) + carry;
    const write = sum % 10;
    const newCarry = Math.floor(sum / 10);
    const place = places[maxLen - 1 - col] || "";
    const carryTxt = carry ? (fr ? ` + ${carry} (retenue)` : ` + ${carry} (carried)`) : "";
    const parts = colDigits.join(" + ") + carryTxt;
    let line = (fr ? `Colonne des ${place} : ` : `${cap(place)}: `) + `${parts} = ${sum}`;
    line += newCarry
      ? fr ? ` → écris ${write}, retiens ${newCarry}` : ` → write ${write}, carry ${newCarry}`
      : fr ? ` → écris ${write}` : ` → write ${write}`;
    steps.push(line);
    carry = newCarry;
  }
  const total = nums.reduce((a, b) => a + b, 0);
  steps.push((fr ? "Réponse : " : "Answer: ") + fmt(total));
  return steps;
}

function subSteps(a, b, fr) {
  const places = PLACES[fr ? "fr" : "en"];
  const maxLen = Math.max(String(a).length, String(b).length);
  const A = String(a).padStart(maxLen, "0").split("").map(Number);
  const B = String(b).padStart(maxLen, "0").split("").map(Number);
  const steps = [];
  let borrow = 0;
  for (let col = maxLen - 1; col >= 0; col--) {
    const top = A[col] - borrow;
    const bot = B[col];
    const place = places[maxLen - 1 - col] || "";
    const pre = fr ? `Colonne des ${place} : ` : `${cap(place)}: `;
    if (top < bot) {
      steps.push(pre + `${top}${borrow ? (fr ? " (après emprunt)" : " (after borrow)") : ""} ${fr ? "trop petit, on emprunte 10" : "too small, borrow 10"}: ${top + 10} - ${bot} = ${top + 10 - bot}`);
      borrow = 1;
    } else {
      steps.push(pre + `${top} - ${bot} = ${top - bot}`);
      borrow = 0;
    }
  }
  steps.push((fr ? "Réponse : " : "Answer: ") + fmt(a - b));
  return steps;
}

function mulSteps(a, b, fr) {
  const places = PLACES[fr ? "fr" : "en"];
  const bStr = String(b);
  const steps = [];
  if (bStr.length === 1) {
    steps.push((fr ? "Multiplie : " : "Multiply: ") + `${a} × ${b} = ${a * b}`);
  } else {
    const rev = bStr.split("").reverse();
    const partials = [];
    rev.forEach((d, j) => {
      const base = a * Number(d);
      const val = base * Math.pow(10, j);
      partials.push(val);
      const placeTxt = j ? (fr ? ` (chiffre des ${places[j]})` : ` (${places[j]} digit)`) : "";
      const zeros = j ? (fr ? ` puis ajoute ${j} zéro${j > 1 ? "s" : ""} → ${fmt(val)}` : ` then add ${j} zero${j > 1 ? "s" : ""} → ${fmt(val)}`) : "";
      steps.push(`${a} × ${d}${placeTxt} = ${fmt(base)}${zeros}`);
    });
    steps.push((fr ? "Additionne : " : "Add: ") + partials.map(fmt).join(" + ") + ` = ${fmt(a * b)}`);
  }
  steps.push((fr ? "Réponse : " : "Answer: ") + fmt(a * b));
  return steps;
}

function divSteps(a, b, fr) {
  const q = Math.floor(a / b);
  const r = a % b;
  const steps = [];
  steps.push((fr ? `Combien de fois ${b} entre dans ${fmt(a)} ? ` : `How many times does ${b} go into ${fmt(a)}? `) + `${q}`);
  steps.push(`${b} × ${q} = ${fmt(b * q)}`);
  steps.push((fr ? "Reste : " : "Remainder: ") + `${fmt(a)} - ${fmt(b * q)} = ${r}`);
  steps.push((fr ? "Réponse : " : "Answer: ") + (r ? `${q} ${fr ? "reste" : "r"} ${r}` : `${q}`));
  return steps;
}

// Returns an array of short step strings, or null if not a plain operation.
export function buildWorking(question, lang = "en") {
  const p = parseArithmetic(question);
  if (!p) return null;
  const fr = lang === "fr";
  const toInt = (x) => parseInt(String(x).replace(/\D/g, ""), 10);
  try {
    if (p.op === "add") {
      const nums = (p.addends || [p.a, p.b]).map(toInt).filter((n) => !isNaN(n));
      if (nums.length < 2) return null;
      return addSteps(nums, fr);
    }
    if (p.op === "sub") return subSteps(toInt(p.a), toInt(p.b), fr);
    if (p.op === "mul") return mulSteps(toInt(p.a), toInt(p.b), fr);
    if (p.op === "div") return divSteps(toInt(p.a), toInt(p.b), fr);
  } catch (e) {
    return null;
  }
  return null;
}
